import { forwardRef, useMemo } from 'react';
import { ProposalBlockEditor } from './ProposalBlockEditor';
import { resolveMergeTagsInText, type MergeTagContext } from './mergeTags';
import { parseCoverData, type Proposal } from '@/lib/proposalService';
import { format } from 'date-fns';
import { CircleCheck as CheckCircle2 } from 'lucide-react';

interface Props {
  proposal: Proposal;
  agencyName?: string;
  agencyLogo?: string;
  sender?: any;
}

export const ProposalRenderer = forwardRef<HTMLElement, Props>(function ProposalRenderer(
  { proposal, agencyName, agencyLogo, sender }: Props,
  ref,
) {
  const name = agencyName || 'Agency';

  const clientFirstName = proposal.client_name?.split(' ')[0] || '';
  const clientLastName = proposal.client_name?.split(' ').slice(1).join(' ') || '';

  const ctx: MergeTagContext = useMemo(() => ({
    client_name: proposal.client_name,
    client_email: proposal.client_email,
    company_name: proposal.company_name,
    agency_name: name,
    today: format(new Date(), 'MMMM d, yyyy'),
    valid_until: proposal.valid_until ? format(new Date(proposal.valid_until), 'MMMM d, yyyy') : '',
    client_first_name: clientFirstName,
    client_last_name: clientLastName,
    client_company: proposal.company_name,
    sender_first_name: sender?.first_name || '',
    sender_last_name: sender?.last_name || '',
    sender_company: name,
    sender_email: sender?.email || '',
    sender_phone: sender?.phone || '',
  }), [proposal, name, sender]);

  const coverInfo = parseCoverData(proposal.cover_image_url);

  return (
    <article ref={ref} className="space-y-0">
      {/* ── Cover – 9:16 ratio ── */}
      <div
        className="relative overflow-hidden mx-auto w-full transition-all"
        style={{ 
          maxWidth: '960px', 
          aspectRatio: '4/5', 
          borderRadius: '4px', 
          background: coverInfo.bgType === 'gradient' && coverInfo.gradient ? coverInfo.gradient : coverInfo.color 
        }}
      >
        {/* Background photo overlay */}
        {coverInfo.image && (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${coverInfo.image})`, opacity: 0.18 }}
          />
        )}

        {/* Top: agency logo */}
        <div className="relative z-10 flex items-start justify-between px-16 sm:px-20 pt-16 sm:pt-20">
          {agencyLogo ? (
            <img src={agencyLogo} alt={name} className="h-20 w-auto object-contain drop-shadow-sm" />
          ) : (
            <img 
              src="/img-placeholder-16-9.png" 
              alt="Logo Placeholder" 
              className="h-20 w-20 object-cover rounded drop-shadow-sm opacity-60" 
            />
          )}

          {proposal.valid_until && proposal.status === 'pending' && (
            <span className="text-xs text-gray-500 pt-1">
              Valid until {format(new Date(proposal.valid_until), 'MMM d, yyyy')}
            </span>
          )}
        </div>

        {/* Title area */}
        <div
          className="relative z-10 flex flex-col justify-center px-16 sm:px-20 pt-24 pb-16"
          style={{ minHeight: '55%' }}
        >
          <h1
            className="leading-none drop-shadow-sm whitespace-pre-wrap"
            style={{
              fontFamily: coverInfo.titleSettings.fontFamily || 'Inter',
              fontSize: `${coverInfo.titleSettings.fontSize}px`,
              letterSpacing: `${coverInfo.titleSettings.letterSpacing}em`,
              textTransform: coverInfo.titleSettings.textCase !== 'none' ? coverInfo.titleSettings.textCase : undefined,
              color: coverInfo.titleSettings.color,
              fontWeight: coverInfo.titleSettings.isBold ? 900 : 400,
              fontStyle: coverInfo.titleSettings.isItalic ? 'italic' : 'normal',
              textDecoration: coverInfo.titleSettings.isUnderline ? 'underline' : 'none',
              textAlign: coverInfo.titleSettings.alignment || 'left',
              lineHeight: coverInfo.titleSettings.lineHeight || 1.05
            }}
          >
            {proposal.title || 'PROJECT PROPOSAL'}
          </h1>
        </div>

        {/* Footer bar */}
        <div
          className="absolute bottom-16 left-16 right-16 sm:bottom-20 sm:left-20 sm:right-20 z-10 p-8 rounded-xl"
          style={{
            background: coverInfo.footerBgType === 'gradient' && coverInfo.footerGradient
              ? coverInfo.footerGradient
              : (coverInfo.footerBgColor || '#ffffff'),
            opacity: coverInfo.footerOpacity !== undefined ? coverInfo.footerOpacity : 1,
          }}
        >
          <div className="grid grid-cols-2 gap-12 relative text-left min-h-[140px]">
            {/* Vertical Divider */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-black/10 -translate-x-1/2" />

            <div className="flex flex-col justify-center gap-3">
              <p style={{
                fontWeight: 'bold',
                fontSize: `${coverInfo.footerStyleSettings?.titleSize || 20}px`,
                color: coverInfo.footerStyleSettings?.titleColor || '#111111',
                lineHeight: 1.2,
              }}>
                {resolveMergeTagsInText(coverInfo.footerSettings?.leftTitle || 'CREATED BY:', ctx)}
              </p>
              <div className="space-y-1">
                {(resolveMergeTagsInText(coverInfo.footerSettings?.leftContent || '', ctx)).split('\n').map((line, i) => (
                  <div key={i} style={{ fontSize: `${coverInfo.footerStyleSettings?.textSize || 14}px`, color: coverInfo.footerStyleSettings?.textColor || '#333333' }}>
                    {line}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col justify-center gap-3 pl-4">
              <p style={{
                fontWeight: 'bold',
                fontSize: `${coverInfo.footerStyleSettings?.titleSize || 20}px`,
                color: coverInfo.footerStyleSettings?.titleColor || '#111111',
                lineHeight: 1.2,
              }}>
                {resolveMergeTagsInText(coverInfo.footerSettings?.rightTitle || 'PREPARED FOR:', ctx)}
              </p>
              <div className="space-y-1">
                {(resolveMergeTagsInText(coverInfo.footerSettings?.rightContent || '', ctx)).split('\n').map((line, i) => (
                  <div key={i} style={{ fontSize: `${coverInfo.footerStyleSettings?.textSize || 14}px`, color: coverInfo.footerStyleSettings?.textColor || '#333333' }}>
                    {line}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Body content ── */}
      <div
        className="bg-white shadow-sm mx-auto"
        style={{ maxWidth: '960px', borderRadius: '4px' }}
      >
        <div className="px-4 sm:px-8 py-10">
          <ProposalBlockEditor content={proposal.content || []} editable={false} mergeContext={ctx} />
        </div>

        {/* Approved signature section */}
        {proposal.status === 'approved' && proposal.signature_data && (
          <div className="border-t border-gray-100 px-8 py-6 bg-emerald-50/40">
            <div className="flex items-center gap-2 text-emerald-700 mb-4">
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">Approved & Signed</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 items-end">
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Signed by</div>
                <div className="font-semibold text-gray-900 text-lg">{proposal.signer_name}</div>
                {proposal.signed_at && (
                  <div className="text-xs text-gray-500 mt-1">
                    {format(new Date(proposal.signed_at), 'PPP p')}
                  </div>
                )}
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3 max-w-xs">
                <img src={proposal.signature_data} alt="Signature" className="w-full h-20 object-contain" />
              </div>
            </div>
          </div>
        )}
      </div>
    </article>
  );
});
